import type { Source } from "../types/cuadra";

/**
 * Metadata stored per message
 * Keyed by message/chat ID to persist across the conversation
 */
export interface MessageMetadata {
  /** RAG sources/citations used to generate the response */
  sources?: Source[];
}

/**
 * Store for per-message metadata (sources)
 * Keyed by message ID so each message retains its own sources
 * Reasoning is handled via native assistant-ui content parts
 */
class StreamingMetadataStore {
  /** Per-message metadata, keyed by message ID */
  private messageMetadata: Map<string, MessageMetadata> = new Map();
  /** Current streaming message ID */
  private currentMessageId: string | null = null;
  /** True while an artifact tool call is streaming (create_artifact/update_artifact args not yet complete) */
  private pendingArtifact = false;
  private listeners: Set<() => void> = new Set();

  /**
   * Set the current streaming message ID
   */
  setCurrentMessage(messageId: string): void {
    this.currentMessageId = messageId;
  }

  setPendingArtifact(value: boolean): void {
    if (this.pendingArtifact === value) return;
    this.pendingArtifact = value;
    this.notify();
  }

  getPendingArtifact(): boolean {
    return this.pendingArtifact;
  }

  /**
   * Update sources for the current message
   */
  update(data: Partial<MessageMetadata>): void {
    if (!this.currentMessageId) return;

    const existing = this.messageMetadata.get(this.currentMessageId) || {};
    const newSources = data.sources;

    // Only update if sources actually changed
    if (newSources && newSources.length > 0) {
      this.messageMetadata.set(this.currentMessageId, {
        ...existing,
        sources: newSources,
      });
      this.notify();
    }
  }

  /**
   * Get metadata for the current streaming message
   */
  get(): MessageMetadata {
    if (!this.currentMessageId) return {};
    return this.messageMetadata.get(this.currentMessageId) || {};
  }

  /**
   * Get metadata for a specific message ID
   */
  getForMessage(messageId: string): MessageMetadata {
    return this.messageMetadata.get(messageId) || {};
  }

  /**
   * Get the current message ID
   */
  getCurrentMessageId(): string | null {
    return this.currentMessageId;
  }

  /**
   * Clear current message (but keep historical metadata)
   * Called when starting a new message stream
   */
  clear(): void {
    this.currentMessageId = null;
    this.pendingArtifact = false;
    // Don't clear messageMetadata - keep historical sources!
  }

  /**
   * Subscribe to changes
   */
  subscribe(listener: () => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  private notify(): void {
    this.listeners.forEach((listener) => listener());
  }
}

// Singleton instance
export const streamingMetadataStore = new StreamingMetadataStore();

// Re-export the old type name for compatibility
export type StreamingMetadata = MessageMetadata;
